import React, { useState } from 'react';
import Box from '../../../../components/Box/Box';
import type { TDropdown } from '../../../../types/TDropdown';
import type { TOption } from '../../../../types/TOption';
import keyboardKeys from '../../../../constants/keyboardKeys';
import getModifiers from '../../utils/getModifiers';
import getLiveRegionText from '../../utils/getLiveRegionText';

const ASC = 'ASC';
const DESC = 'DESC';

const { ENTER, ARROW_RIGHT, ESCAPE, ARROW_DOWN, ARROW_UP, TAB } = keyboardKeys;

function getNextActiveIndex(order: string, items: TOption[], activeIdx: number): number {
  const idx = order === DESC && activeIdx === -1 ? items.length : activeIdx;

  const clickableItems = items.map(
    (item, index) => item.clickable !== false && (order === ASC ? index > idx : index < idx),
  );

  const startingPoint = order === ASC ? 'indexOf' : 'lastIndexOf';
  return clickableItems[startingPoint](true);
}

function DropdownOptions({ options, handleItemClick, handleItemMouseDown, activeItemIdx }: any) {
  return options.map((item, key) => {
    /*
    clickable, searchLink, otherCriteria are not actively used,
    is it possible this pattern is used to remove them from ...rest?
    @TODO: refactor?
    */

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { value, label, modifiers = [], clickable, searchLink, otherCriteria, ...rest } = item;
    const arrModifiers = getModifiers(modifiers, key, activeItemIdx);

    return (
      <Box
        {...rest}
        value={String(value)}
        key={key}
        as="li"
        parent="Nav"
        name="pill"
        role="option"
        modifiers={arrModifiers}
        onMouseDown={handleItemMouseDown}
        onClick={handleItemClick(item, true)}
      >
        {label}
      </Box>
    );
  });
}

const Dropdown = ({
  as = 'div',
  containerClassName,
  containerModifiers = [],
  toggleClassName,
  options,
  text,
  toggleOnClick = true,
  translations,
  ariaLabel,
  onKeyDown,
  onFocus,
  onBlur,
  onMouseDown,
  onItemNavigation,
  onItemSelect,
  ...props
}: TDropdown) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeItemIdx, setActiveItemIdx] = useState(-1);

  // set and return new active index
  const setActive = (order: string) => {
    const idx = getNextActiveIndex(order, options, activeItemIdx);
    setActiveItemIdx(idx);

    return idx;
  };

  const handleItemNavigation = (ORDER?: string) => {
    const idx = ORDER ? setActive(ORDER) : '';

    if (onItemNavigation) {
      const item: TOption = options[idx];
      onItemNavigation(item);
    }
  };

  const handleItemSelect = (item?: any, isSubmit = false) => {
    if (onItemSelect) {
      onItemSelect(item || options[activeItemIdx], isSubmit);
      setIsOpen(false);
    }

    // reset an active index if arrow navigation has happened
    setActiveItemIdx(-1);
  };

  const handleFocus = (event: React.FocusEvent<any, Element>) => {
    setIsOpen(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<any, Element>) => {
    setIsOpen(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleMouseDown = (event: React.MouseEvent<any, MouseEvent>) => {
    if (toggleOnClick) setIsOpen(!isOpen);
    if (onMouseDown) onMouseDown(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<any>) => {
    switch (event.key) {
      case TAB:
        setIsOpen(false);
        break;

      case ENTER:
        handleItemSelect(null, true);
        if (toggleOnClick) {
          setIsOpen(!isOpen);
        }
        handleItemSelect();
        break;

      case ARROW_RIGHT:
        handleItemSelect();
        break;

      case ESCAPE:
        setIsOpen(false);
        setActiveItemIdx(-1);
        handleItemNavigation();
        break;

      case ARROW_DOWN:
        if (!isOpen) {
          setIsOpen(true);
        }
        handleItemNavigation(ASC);
        break;

      case ARROW_UP:
        if (isOpen) {
          handleItemNavigation(DESC);
        }
        break;

      default:
        if (!isOpen) {
          setIsOpen(true);
        }
        break;
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const handleItemMouseDown = (event: React.MouseEvent<any, MouseEvent>) => {
    event.preventDefault();
  };

  const handleItemClick = (item: TOption, isSubmit: boolean) => (event: React.MouseEvent<any, MouseEvent>) => {
    if (item.onClick) item.onClick();

    if (item && item.clickable === false) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }

    handleItemSelect(item, isSubmit);
  };

  const liveRegionText = getLiveRegionText(options, activeItemIdx, translations);
  return (
    <Box
      parent="Nav"
      name="dropdown"
      as="div"
      className={containerClassName}
      modifiers={[...containerModifiers, isOpen ? 'open' : 'closed']}
    >
      <Box
        {...props}
        as={as}
        parent="Nav-dropdown"
        name="toggle"
        className={toggleClassName}
        tabIndex={0}
        role="combobox" // Indicates that this is a combobox (input with suggestions)
        aria-expanded={isOpen} // Indicates whether the dropdown is expanded or collapsed.
        aria-owns="dropdown-list" // Indicates the ID of the dropdown list.
        aria-label={ariaLabel}
        aria-describedby="autocomplete-instructions" // Describes the purpose of the combobox
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseDown={handleMouseDown}
      >
        {text}
      </Box>

      <div id="autocomplete-instructions" style={{ display: 'none' }}>
        {translations?.dropdownInstructions}
      </div>

      {/* Accessibility live region: announce number of items and selected item */}
      <div aria-live="polite" style={{ position: 'absolute', height: 0, width: 0, overflow: 'hidden' }}>
        {liveRegionText}
      </div>

      {isOpen && (
        <Box as="ul" parent="Nav-dropdown" name="menu" id="dropdown-list" role="listbox">
          <DropdownOptions
            options={options}
            handleItemClick={handleItemClick}
            handleItemMouseDown={handleItemMouseDown}
            activeItemIdx={activeItemIdx}
          />
        </Box>
      )}
    </Box>
  );
};

export default Dropdown;
